import { motion } from "framer-motion";
import { Quote, Sparkles, Linkedin } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const VIDEO_TESTIMONIAL = {
  id: "sZrRNRc5dKI",
  name: "Avinash Devadanam",
  role: "Director, Eloi Training",
  quote:
    "Took the WeKIT™ Test and found it remarkable — real clarity on purpose and pathway.",
};

const TEXT_TESTIMONIALS = [
  {
    name: "Berlin Immanuel Santhosh",
    role: "AI/ML Developer · LLMs, RAG Pipelines, Agentic Systems",
    initials: "BS",
    quote:
      "I just completed the WeKIT™ Career Clarity 360 assessment, and what surprised me most wasn't just the career recommendations — it was how clearly it connected my strengths, interests, and personality into a practical career pathway. Within about 20 minutes, I had a personalised roadmap that helped me see not just what careers could fit me, but why.",
    href: "https://lnkd.in/gist8vdX",
    tags: ["#CareerClarity", "#FutureReady", "#PurposeDriven", "#AIforGood"],
  },
  {
    name: "Sumit KD",
    role: "Parent · AI Enthusiast | Lipi.Study — Language-led EdTech",
    initials: "SK",
    quote:
      "Tried WeKIT.ai's Career Clarity assessment with my son today — genuinely impressive. In under 30 minutes it gave him something most career guidance doesn't: real clarity. A dream board of what he's actually working toward, built from his own strengths and interests, not a generic quiz. It maps neatly onto CBSE (Class 10) and state board frameworks, and ICSE (Class 8) — but honestly this isn't just for students. Anyone figuring out their own direction would benefit. Great work, Yvonne Roberts. Every child in India should have access to something like this.",
    tags: ["#ParentVoice", "#CareerClarity", "#CBSE", "#ICSE"],
  },
];

export default function Testimonials() {
  return (
    <section
      id="testimonials"
      aria-labelledby="testimonials-heading"
      className="relative overflow-hidden border-t border-border/40 bg-gradient-to-b from-background via-background to-primary/[0.03] py-20 sm:py-28"
    >
      <div className="container mx-auto px-6">
        <div className="mx-auto max-w-2xl text-center">
          <Badge
            variant="outline"
            className="mb-5 rounded-full border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-semibold text-primary"
          >
            <Sparkles className="mr-1.5 h-3 w-3" /> Real stories
          </Badge>
          <h2
            id="testimonials-heading"
            className="font-display text-3xl font-bold tracking-tight sm:text-5xl"
          >
            Voices from the <span className="gradient-text">WeKIT™</span> journey
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Hear directly from learners whose careers, confidence and clarity
            changed with WeKIT™ Career Clarity 360.
          </p>
        </div>

        {/* Video testimonial */}
        <div className="mt-14">
          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="mx-auto flex max-w-5xl flex-col items-center gap-8 lg:flex-row lg:items-stretch lg:justify-center"
          >
            <div className="relative shrink-0">
              <div className="absolute -inset-3 -z-10 rounded-[2rem] bg-gradient-to-br from-primary/30 via-accent/20 to-primary/10 blur-2xl" />
              <div className="relative aspect-[9/16] w-[280px] overflow-hidden rounded-3xl border border-border/60 bg-black shadow-2xl shadow-primary/10 sm:w-[320px]">
                <iframe
                  src={`https://www.youtube.com/embed/${VIDEO_TESTIMONIAL.id}?rel=0&modestbranding=1&playsinline=1`}
                  title={`${VIDEO_TESTIMONIAL.name} — WeKIT™ testimonial`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  className="h-full w-full"
                />
              </div>
            </div>

            <div className="flex max-w-md flex-col justify-center text-center lg:text-left">
              <Quote className="mx-auto h-8 w-8 text-primary/60 lg:mx-0" />
              <blockquote className="mt-4 font-display text-2xl font-semibold leading-snug text-foreground sm:text-3xl">
                {VIDEO_TESTIMONIAL.quote}
              </blockquote>
              <figcaption className="mt-6 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{VIDEO_TESTIMONIAL.name}</span>
                <span className="mx-2">·</span>
                <span>{VIDEO_TESTIMONIAL.role}</span>
              </figcaption>
              <div className="mt-4">
                <a
                  href={`https://www.youtube.com/shorts/${VIDEO_TESTIMONIAL.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-primary hover:underline"
                >
                  Watch on YouTube →
                </a>
              </div>
            </div>
          </motion.figure>
        </div>

        {/* Written testimonials */}
        <div className="mx-auto mt-16 grid max-w-5xl gap-6 sm:grid-cols-1 lg:grid-cols-2">
          {TEXT_TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={t.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.1, ease: "easeOut" }}
              className="group relative flex flex-col rounded-3xl border border-border/60 bg-card/60 p-7 shadow-lg shadow-primary/[0.04] backdrop-blur transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 sm:p-8"
            >
              <div className="absolute right-6 top-6 text-[#0a66c2]/80">
                <Linkedin className="h-5 w-5" />
              </div>
              <Quote className="h-7 w-7 text-primary/50" />
              <blockquote className="mt-4 text-base leading-relaxed text-foreground sm:text-lg">
                {t.quote}
              </blockquote>
              {t.tags && (
                <div className="mt-5 flex flex-wrap gap-1.5">
                  {t.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-primary/8 px-2.5 py-0.5 text-[11px] font-medium text-primary/80"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <figcaption className="mt-6 flex items-center gap-3 border-t border-border/50 pt-5">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-accent/20 text-sm font-bold text-primary">
                  {t.initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-foreground">{t.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{t.role}</div>
                </div>
                {t.href && (
                  <a
                    href={t.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-primary hover:underline"
                    aria-label={`Read ${t.name}'s post on LinkedIn`}
                  >
                    View →
                  </a>
                )}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
